import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { InventoryItem } from '../../utils/types';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.png';

// واجهة الإعلان الترويجي
export interface Promotion {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  backgroundColor?: string;
  textColor?: string;
  buttonText?: string;
  buttonLink?: string;
  productId?: string;
  product?: InventoryItem;
  position?: 'left' | 'right';
  size?: 'small' | 'medium' | 'large';
  // إضافة خصائص الخط
  titleFontSize?: string;  // حجم خط العنوان
  titleFontFamily?: string;  // نوع خط العنوان
  descriptionFontSize?: string;  // حجم خط الوصف
  descriptionFontFamily?: string;  // نوع خط الوصف
}

interface ProductPromotionProps {
  promotions: Promotion[];
  loading?: boolean;
}

const defaultPromotions: Promotion[] = [
  {
    id: '1',
    title: 'أجهزة منزلية بخصم 20%',
    description: 'تسوق الآن واحصل على خصم حصري على جميع الأجهزة المنزلية',
    backgroundColor: '#f44336',
    textColor: '#ffffff',
    buttonText: 'تسوق الآن',
    buttonLink: '/store/categories',
    position: 'right',
    size: 'medium'
  },
  {
    id: '2',
    title: 'أحدث الهواتف الذكية',
    description: 'اكتشف مجموعتنا الجديدة من الهواتف الذكية بأفضل الأسعار',
    backgroundColor: '#2196f3',
    textColor: '#ffffff',
    buttonText: 'اكتشف المزيد',
    buttonLink: '/product/smartphone',
    position: 'left',
    size: 'medium'
  }
];

const ProductPromotion: React.FC<ProductPromotionProps> = ({
  promotions = defaultPromotions,
  loading = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { settings } = useStoreSettings();
  const [isVisible, setIsVisible] = useState<boolean | null>(null);
  const [displayPromotions, setDisplayPromotions] = useState<Promotion[]>(promotions);
  const initialRender = useRef(true);

  // عند تحميل المكون للمرة الأولى، نستخدم قيمة null لتجنب وميض الإعلانات
  useEffect(() => {
    // تحقق من إعدادات ظهور الإعلانات الترويجية فقط عند التحميل الأول
    if (initialRender.current && settings?.display) {
      setIsVisible(settings.display.showPromotions !== false);
      initialRender.current = false;
    }
  }, [settings]);

  // تحديث الإعلانات الترويجية عند تغيير props
  useEffect(() => {
    if (promotions && promotions.length > 0) {
      setDisplayPromotions(promotions);
    }
  }, [promotions]);

  // إذا كانت قيمة الرؤية false (الإخفاء المتعمد من الإعدادات)، لا نعرض شيئًا
  if (isVisible === false) {
    return null;
  }

  // إذا كان قيد التحميل، نعرض skeleton
  if (loading) {
    return (
      <Box sx={{ width: '100%', my: 4 }}>
        <Grid container spacing={3}>
          {[1, 2].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Skeleton variant="rectangular" width="100%" height={200} animation="wave" />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // إذا لم يكن هناك إعلانات للعرض
  if (!displayPromotions.length) {
    return null;
  }

  // تحديد حجم العنصر في الشبكة
  const getGridSize = (size?: string) => {
    switch (size) {
      case 'small':
        return { xs: 12, md: 4 };
      case 'large':
        return { xs: 12, md: 12 };
      case 'medium':
      default:
        return { xs: 12, md: 6 };
    }
  };

  return (
    <Box sx={{ width: '100%', my: 4 }}>
      <Grid container spacing={3}>
        {displayPromotions.map((promo) => {
          const gridSize = getGridSize(promo.size);

          return (
            <Grid item {...gridSize} key={promo.id}>
              <Paper
                sx={{
                  position: 'relative',
                  height: { xs: 200, md: promo.size === 'large' ? 400 : 200 },
                  overflow: 'hidden',
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundImage: promo.imageUrl
                    ? `url(${promo.imageUrl})`
                    : 'none',
                  backgroundColor: promo.backgroundColor || theme.palette.primary.main,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)'
                  }
                }}
              >
                {promo.product && (
                  <Box
                    component="img"
                    src={promo.product.imageUrl || placeholderImage}
                    alt={promo.product.name}
                    sx={{
                      position: 'absolute',
                      [promo.position === 'left' ? 'right' : 'left']: { xs: '50%', md: 0 },
                      top: '50%',
                      transform: {
                        xs: 'translate(50%, -50%)',
                        md: promo.position === 'left'
                          ? 'translate(0, -50%)'
                          : 'translate(0, -50%)'
                      },
                      height: '80%',
                      maxWidth: '50%',
                      objectFit: 'contain',
                      opacity: { xs: 0.2, md: 1 }
                    }}
                  />
                )}

                <Box
                  sx={{
                    p: 3,
                    width: '100%',
                    textAlign: { xs: 'center', md: promo.position || 'left' },
                    [promo.position === 'left' ? 'marginRight' : 'marginLeft']: { md: '50%' },
                    zIndex: 1,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                  }}
                >
                  <Typography
                    variant={isMobile ? 'h5' : 'h4'}
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: promo.textColor || 'white',
                      fontSize: promo.titleFontSize || (isMobile ? '1.5rem' : '2rem'),
                      fontFamily: promo.titleFontFamily || 'Cairo, sans-serif'
                    }}
                  >
                    {promo.title}
                  </Typography>

                  {promo.description && (
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 2,
                        color: promo.textColor || 'white',
                        fontSize: promo.descriptionFontSize || '1rem',
                        fontFamily: promo.descriptionFontFamily || 'Cairo, sans-serif'
                      }}
                    >
                      {promo.description}
                    </Typography>
                  )}

                  {promo.buttonText && (
                    <Button
                      variant="contained"
                      component={RouterLink}
                      to={promo.buttonLink || (promo.productId ? `/product/${promo.productId}` : '/')}
                      sx={{
                        mt: 1,
                        backgroundColor: 'white',
                        color: promo.backgroundColor || theme.palette.primary.main,
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)'
                        }
                      }}
                    >
                      {promo.buttonText}
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ProductPromotion;
